import {
  forwardRef,
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { X } from 'lucide-react';
import type { Role } from '@foodstyles/shared';
import { cn } from '../lib/cn';
import { avatarColor, initials } from '../lib/colors';

// ── Buttons ──────────────────────────────────────────────────────────────────
type BtnVariant = 'primary' | 'danger' | 'secondary' | 'ghost' | 'subtle';
type BtnSize = 'sm' | 'md' | 'lg';

const BTN_SIZE: Record<BtnSize, string> = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3.5 text-sm',
  lg: 'h-11 px-5 text-sm',
};
const BTN_VARIANT: Record<BtnVariant, string> = {
  primary: 'bg-[#FF5A3C] text-white shadow-sm hover:bg-[#ef4a2d]',
  danger: 'bg-rose-600 text-white hover:bg-rose-700',
  secondary:
    'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700',
  ghost: 'text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700',
  subtle: 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600',
};

export function Btn({
  variant = 'secondary',
  size = 'md',
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: BtnSize }) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A3C]/40 disabled:cursor-not-allowed disabled:opacity-50',
        BTN_SIZE[size],
        BTN_VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Inputs ───────────────────────────────────────────────────────────────────
export const inputCls =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-[#FF5A3C] focus:ring-2 focus:ring-[#FF5A3C]/20 focus:outline-none disabled:bg-slate-50 disabled:text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:disabled:bg-slate-800 dark:disabled:text-slate-500';

const invalidCls = 'border-rose-400 ring-2 ring-rose-200 dark:border-rose-500 dark:ring-rose-500/30';

export const TextInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function TextInput({ className, invalid, ...rest }, ref) {
    return <input ref={ref} className={cn(inputCls, invalid && invalidCls, className)} {...rest} />;
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }>(
  function Select({ className, invalid, children, ...rest }, ref) {
    return (
      <select ref={ref} className={cn(inputCls, invalid && invalidCls, className)} {...rest}>
        {children}
      </select>
    );
  },
);

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400 dark:text-slate-500">{hint}</span>}
    </label>
  );
}

// ── Card / titles ─────────────────────────────────────────────────────────────
export function Card({
  pad = true,
  className,
  children,
}: {
  pad?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800', pad && 'p-5', className)}>{children}</div>
  );
}

export function SectionTitle({ children, sub }: { children: ReactNode; sub?: string }) {
  return (
    <div>
      <h2 className="text-[15px] font-bold text-slate-800 dark:text-slate-100">{children}</h2>
      {sub && <p className="text-[11px] text-slate-400 dark:text-slate-500">{sub}</p>}
    </div>
  );
}

// ── Badges ────────────────────────────────────────────────────────────────────
const ROLE_STYLE: Record<Role, string> = {
  Admin: 'bg-[#FF5A3C]/12 text-[#D8341A] dark:text-[#FF8A6B]',
  Manager: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
  Member: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
};
export function RoleBadge({ role, sm }: { role: Role; sm?: boolean }) {
  return (
    <span className={cn('inline-flex items-center rounded-full font-semibold', sm ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs', ROLE_STYLE[role])}>
      {role}
    </span>
  );
}

const STATUS_STYLE: Record<string, string> = {
  Pending: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
  Approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
  Rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300',
};
export function StatusPill({ status }: { status: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold', STATUS_STYLE[status] ?? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300')}>
      {status}
    </span>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────────
export function Avatar({ name, size = 36, grayscale }: { name: string; size?: number; grayscale?: boolean }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-white"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: avatarColor(name),
        filter: grayscale ? 'grayscale(1) opacity(0.6)' : undefined,
      }}
    >
      {initials(name)}
    </span>
  );
}

// ── Modal (overlay + blur, Esc / overlay-click close, focus trap) ───────────────
export function Modal({
  open,
  onClose,
  title,
  width = 'max-w-lg',
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  width?: string;
  children: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<Element | null>(null);
  // Keep onClose in a ref so the focus-trap effect below only re-runs on open/close,
  // not whenever the parent passes a fresh onClose (e.g. a parent that re-renders
  // every second would otherwise re-run this effect and steal focus each tick).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    lastFocused.current = document.activeElement;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      if (e.key === 'Tab') {
        const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
      (lastFocused.current as HTMLElement | null)?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-sm dark:bg-slate-950/60 sm:items-center"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn('w-full rounded-2xl bg-white shadow-xl dark:bg-slate-800', width)}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 dark:border-slate-700">
          <h3 className="text-[15px] font-bold text-slate-800 dark:text-slate-100">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

// ── Misc ──────────────────────────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-[#FF5A3C] dark:border-slate-600 dark:border-t-[#FF5A3C]', className)}
      aria-label="Loading"
    />
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-sm text-slate-400">{children}</p>;
}
